import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Menu, X, Facebook, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { businessInfo } from '../../data/mock';
import { useQuote } from '../../context/QuoteContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { openQuote } = useQuote();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Careers', path: '/careers' },
    { name: 'Gallery', path: '/gallery' },
    { name: "FAQ's", path: '/faqs' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Bar */}
      <div className="bg-[#1a1a1a] text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 hover:text-[#e85a24] transition-colors">
              <Phone size={14} />
              <span>{businessInfo.phone}</span>
            </a>
            <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 hover:text-[#e85a24] transition-colors">
              <Mail size={14} />
              <span>{businessInfo.email}</span>
            </a>
            <a 
              href="https://www.google.com/maps/dir//Basin+Plumbing,+3730+Kermit+Hwy,+Odessa,+TX+79764"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-[#e85a24] transition-colors"
            >
              <MapPin size={14} />
              <span>Odessa, TX 79764</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[#e85a24]" />
              <span>24/7 Emergency Service</span>
            </div>
            <a 
              href={businessInfo.social.facebook} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-[#e85a24] transition-colors"
              aria-label="Facebook"
            >
              <Facebook size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-lg py-2' 
          : 'bg-white/95 backdrop-blur-sm py-3'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="/images/basin-plumbing-logo.png" 
                alt="Basin Plumbing Logo" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#e85a24] bg-orange-50'
                      : 'text-gray-700 hover:text-[#e85a24] hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <a href={`tel:${businessInfo.phone}`} className="text-[#1a1a1a] font-semibold hover:text-[#e85a24] transition-colors">
                {businessInfo.phone}
              </a>
              <Button onClick={() => openQuote()} className="bg-[#e85a24] hover:bg-[#d14a14] text-white px-6" data-testid="header-get-quote-button">
                Get a Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 py-4 bg-white border-t">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#e85a24] bg-orange-50'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t space-y-3">
              <a 
                href={`tel:${businessInfo.phone}`}
                className="flex items-center gap-2 px-4 py-2 text-[#1a1a1a] font-semibold"
              >
                <Phone size={18} className="text-[#e85a24]" />
                {businessInfo.phone}
              </a>
              <Button onClick={() => openQuote()} className="w-full bg-[#e85a24] hover:bg-[#d14a14] text-white" data-testid="header-mobile-get-quote-button">
                Get a Quote
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
